import { A11yModule } from '@angular/cdk/a11y';
import { CommonModule } from '@angular/common';
import { Component, input, signal } from '@angular/core';
import { FeatureCollection } from 'geojson';
import { LngLatLike } from 'mapbox-gl';
import {
  ControlComponent,
  MapComponent,
  MarkerComponent,
  NavigationControlDirective,
} from 'ngx-mapbox-gl';
import { SkeletonModule } from 'primeng/skeleton';

function centroidOfRing(ring: number[][]): [number, number] {
  let sumLng = 0;
  let sumLat = 0;
  for (const [lng, lat] of ring) {
    sumLng += lng;
    sumLat += lat;
  }
  return [sumLng / ring.length, sumLat / ring.length];
}

/** Extract [lng, lat] from area data in any coordinate format. */
function extractPointFromArea(data: any): [number, number] | null {
  if (!data) return null;

  // GeoJSON Point
  if (data.type === 'Point' && data.coordinates?.length === 2) {
    return data.coordinates as [number, number];
  }

  if (!Array.isArray(data) || data.length === 0) return null;

  // [lng, lat]
  if (typeof data[0] === 'number') {
    return data as [number, number];
  }

  // Polygon ring: [[lng, lat], [lng, lat], ...]
  if (Array.isArray(data[0]) && typeof data[0][0] === 'number') {
    return centroidOfRing(data as number[][]);
  }

  // Polygon: [[[lng, lat], ...]]
  if (
    Array.isArray(data[0]) &&
    Array.isArray(data[0][0]) &&
    typeof data[0][0][0] === 'number'
  ) {
    return centroidOfRing(data[0] as number[][]);
  }

  // MultiPolygon or array of Polygons: take first
  if (
    Array.isArray(data[0]) &&
    Array.isArray(data[0][0]) &&
    Array.isArray(data[0][0][0])
  ) {
    return centroidOfRing(data[0][0] as number[][]);
  }

  return null;
}

@Component({
  selector: 'lib-tender-map-v2',
  imports: [
    CommonModule,
    MapComponent,
    SkeletonModule,
    ControlComponent,
    NavigationControlDirective,
    A11yModule,
    MarkerComponent,
  ],
  templateUrl: './tender-map-v2.component.html',
  styles: `
    mgl-map {
      height: 100%;
      width: 100%;
    }
  `,
})
export class TenderMapV2Component {
  ready = signal(false);

  center = input<LngLatLike | null>();
  area = input<any>();
  tenderLots = input<FeatureCollection | null>();
  minZoom = input<number>(5);
  zoomDisabled = input<boolean>(false);

  onMapLoad(map: MapComponent) {
    const { mapInstance } = map;

    let coords = this.center() as [number, number] | null;

    if (!coords) {
      coords = extractPointFromArea(this.area());
    }

    if (coords) {
      mapInstance.jumpTo({ center: coords });
    }

    this.ready.set(true);
  }
}
